import React, { useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2, X } from 'lucide-react';
import { getError } from '../../utils';
import { useTranslation } from 'react-i18next';
import rifaService from '../../services/rifa.service';
import settingsService from '../../services/settings.service';
import ConfirmModal from '../../components/ConfirmModal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import PageHeader from '../../components/shared/PageHeader';
import LotteryAcknowledgment from '../../components/LotteryAcknowledgment/LotteryAcknowledgment';
import { BoardModeFields, SorteosPickerFields } from '../../components/rifa/RifaModeFields';
import { computeBoardTotalTickets, toTicketsPerSquare } from '../../lib/boardPlans';

const reducer = (state, action) => {
  switch (action.type) {
    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return { ...state, loadingCreate: false };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };
    default:
      return state;
  }
};

const emptyCategory = {
  nombre: '',
  color: '#FFD700',
  filas: 4,
  columnas: 7,
  precioCasilla: 100,
  ticketsPorCasilla: 1,
};

const emptySorteo = { titulo: '', fecha: '', objetivoVenta: 0, premios: [] };
const emptyPremio = { nombre: '', descripcion: '', puesto: 1 };

function AddRifa() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState('standard');
  const [ticketFocus, setTicketFocus] = useState('sorteos');
  const [totalTickets, setTotalTickets] = useState(0);
  const [precioTicket, setPrecioTicket] = useState(200);
  const [ticketImage, setTicketImage] = useState();
  const [rifaImage, setRifaImage] = useState();
  const [imagenTableroVendido, setImagenTableroVendido] = useState();
  const [sorteosEnabled, setSorteosEnabled] = useState(true);
  const [pickerMode, setPickerMode] = useState('both');
  const [mostrarNumeros, setMostrarNumeros] = useState(true);
  const [boardMode, setBoardMode] = useState('separate');
  const [sharedBoard, setSharedBoard] = useState({ filas: 4, columnas: 7 });
  const isSharedBoard = tipo === 'board' && boardMode === 'shared';

  // Sorteo state
  const [sorteos, setSorteos] = useState([]);
  const [currentSorteo, setCurrentSorteo] = useState({ ...emptySorteo });
  const [editingSorteoIndex, setEditingSorteoIndex] = useState(null);

  // Premio state
  const [currentPremio, setCurrentPremio] = useState({ ...emptyPremio });
  const [editingPremioIndex, setEditingPremioIndex] = useState(null);

  // Board-specific state
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState({ ...emptyCategory });
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const handleClose = () => setShowModal(false);

  const [, dispatch] = useReducer(reducer, { loadingCreate: false });

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
    if (tipo === 'board' && categories.length === 0) {
      toast.error(t('raffle.boardNeedsCategories') || 'Add at least one category to the board');
      return;
    }
    setShowModal(true);
  };

  const addRifaHandler = async () => {
    try {
      dispatch({ type: 'CREATE_REQUEST' });
      await rifaService.createRifa({
        nombre,
        descripcion,
        tipo,
        ticketFocus,
        ticketImage,
        rifaImage,
        imagenTableroVendido: tipo === 'board' ? imagenTableroVendido : undefined,
        totalTickets: tipo === 'board' ? 0 : totalTickets,
        precioTicket: tipo === 'board' ? 0 : precioTicket,
        sorteos,
        sorteosEnabled,
        // Hidden numbers force the random picker (the server normalizes it too)
        pickerMode: mostrarNumeros ? pickerMode : 'random',
        mostrarNumeros,
        config:
          tipo === 'board'
            ? { boardMode, categories, ...(isSharedBoard ? { sharedBoard } : {}) }
            : {},
      });
      toast.success(t('raffle.raffleCreated'));
      navigate('/AdminScreen/rifas');
    } catch (ex) {
      toast.error(getError(ex));
    }
  };

  // ── Sorteo handlers ──────────────────────────────────────────────────────
  const handleSaveSorteo = () => {
    if (!currentSorteo.titulo || !currentSorteo.fecha) {
      toast.error(t('raffle.sorteoRequiredFields'));
      return;
    }
    const entry = {
      titulo: currentSorteo.titulo,
      fecha: currentSorteo.fecha,
      objetivoVenta: currentSorteo.objetivoVenta,
      premios: [...currentSorteo.premios],
    };
    if (editingSorteoIndex !== null) {
      const updated = [...sorteos];
      updated[editingSorteoIndex] = entry;
      setSorteos(updated);
    } else {
      setSorteos([...sorteos, entry]);
    }
    clearCurrentSorteo();
  };

  const handleEditSorteo = index => {
    setEditingSorteoIndex(index);
    setCurrentSorteo({ ...sorteos[index], premios: [...sorteos[index].premios] });
    setEditingPremioIndex(null);
    setCurrentPremio({ ...emptyPremio });
  };

  const handleRemoveSorteo = index => {
    setSorteos(sorteos.filter((_, i) => i !== index));
    if (editingSorteoIndex === index) clearCurrentSorteo();
  };

  // ── Premio handlers ──────────────────────────────────────────────────────
  const handleSavePremio = () => {
    if (!currentPremio.nombre || !currentPremio.descripcion) {
      toast.error(t('raffle.premioRequiredFields'));
      return;
    }
    if (editingPremioIndex !== null) {
      const updated = [...currentSorteo.premios];
      updated[editingPremioIndex] = { ...currentPremio };
      setCurrentSorteo({ ...currentSorteo, premios: updated });
      setEditingPremioIndex(null);
    } else {
      setCurrentSorteo({
        ...currentSorteo,
        premios: [...currentSorteo.premios, { ...currentPremio }],
      });
    }
    setCurrentPremio({ nombre: '', descripcion: '', puesto: currentPremio.puesto + 1 });
  };

  const handleEditPremio = index => {
    setEditingPremioIndex(index);
    setCurrentPremio({ ...currentSorteo.premios[index] });
  };

  const handleRemovePremio = index => {
    setCurrentSorteo({
      ...currentSorteo,
      premios: currentSorteo.premios.filter((_, i) => i !== index),
    });
    if (editingPremioIndex === index) {
      setEditingPremioIndex(null);
      setCurrentPremio({ ...emptyPremio });
    }
  };

  const clearCurrentSorteo = () => {
    setCurrentSorteo({ ...emptySorteo });
    setCurrentPremio({ ...emptyPremio });
    setEditingSorteoIndex(null);
    setEditingPremioIndex(null);
  };

  // ── Category handlers ────────────────────────────────────────────────────
  const normalizeCategory = cat => ({
    nombre: cat.nombre || '',
    color: cat.color || '#FFD700',
    filas: Math.max(1, Number(cat.filas) || 1),
    columnas: Math.max(1, Number(cat.columnas) || 1),
    precioCasilla: Math.max(1, Number(cat.precioCasilla) || 1),
    ticketsPorCasilla: toTicketsPerSquare(cat.ticketsPorCasilla),
  });

  const handleEditCategory = index => {
    setCurrentCategory(normalizeCategory(categories[index]));
    setEditingCategoryIndex(index);
  };

  const cancelCategoryEdit = () => {
    setEditingCategoryIndex(null);
    setCurrentCategory({ ...emptyCategory });
  };

  const handleSaveCategory = () => {
    if (isSharedBoard && !String(currentCategory.nombre || '').trim()) {
      toast.error(t('raffle.planRequiredFields', 'El nombre del plan es obligatorio'));
      return;
    }
    if (!currentCategory.nombre || currentCategory.filas < 1 || currentCategory.columnas < 1) {
      toast.error(
        t('raffle.categoryRequiredFields') || 'Category name, rows and columns are required'
      );
      return;
    }
    const payload = normalizeCategory(currentCategory);
    if (editingCategoryIndex !== null) {
      const next = [...categories];
      next[editingCategoryIndex] = payload;
      setCategories(next);
      setEditingCategoryIndex(null);
    } else {
      setCategories([...categories, payload]);
    }
    setCurrentCategory({ ...emptyCategory });
  };

  const handleRemoveCategory = index => {
    setCategories(categories.filter((_, i) => i !== index));
    if (editingCategoryIndex === index) {
      setEditingCategoryIndex(null);
      setCurrentCategory({ ...emptyCategory });
    } else if (editingCategoryIndex !== null && index < editingCategoryIndex) {
      setEditingCategoryIndex(editingCategoryIndex - 1);
    }
  };

  const computedTotalTickets = computeBoardTotalTickets({ boardMode, sharedBoard, categories });

  const uploadPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      let r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      if (ticketImage) formData.append('documentId', ticketImage._id);
      formData.append('type', 'ticketImage');
      const response = await settingsService.uploadSettingsImg(formData);
      setTicketImage(response);
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const uploadRifaPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      let r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      if (rifaImage) formData.append('documentId', rifaImage._id);
      formData.append('type', 'rifaImage');
      const response = await settingsService.uploadSettingsImg(formData);
      setRifaImage(response);
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const uploadBoardSoldPicture = async event => {
    try {
      const file = event.target.files[0];
      const formData = new FormData();
      const r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      if (imagenTableroVendido) formData.append('documentId', imagenTableroVendido._id);
      formData.append('type', 'imagenTableroVendido');
      const response = await settingsService.uploadSettingsImg(formData);
      setImagenTableroVendido(response);
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  return (
    <div className='space-y-6'>
      <Helmet>
        <title>{t('raffle.newRaffle')}</title>
      </Helmet>

      <ConfirmModal
        show={showModal}
        title={t('common.saveChanges')}
        message={t('common.confirmSaveChanges')}
        onConfirm={addRifaHandler}
        onCancel={handleClose}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
      />

      <PageHeader
        title={t('raffle.newRaffle')}
        actions={
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => navigate(-1)}>
              <ArrowLeft className='h-4 w-4' />
              {t('common.back')}
            </Button>
            <Button type='submit' form='rifaMainForm'>
              {t('common.saveChanges')}
            </Button>
          </div>
        }
      />

      <form id='rifaMainForm' onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('raffle.newRaffle')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {/* Raffle type */}
            <div className='space-y-1.5'>
              <label className='text-sm font-medium text-foreground'>
                <strong>{t('raffle.type') || 'Voucher Type'}</strong>
              </label>
              <select
                className='h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                value={tipo}
                onChange={e => setTipo(e.target.value)}
              >
                <option value='standard'>
                  {t('raffle.typeStandard') || 'Standard (numbered tickets)'}
                </option>
                <option value='board'>{t('raffle.typeBoard') || 'Board (Colaboradores)'}</option>
              </select>
            </div>

            <SorteosPickerFields
              t={t}
              sorteosEnabled={sorteosEnabled}
              onSorteosChange={setSorteosEnabled}
              pickerMode={pickerMode}
              onPickerChange={setPickerMode}
              mostrarNumeros={mostrarNumeros}
              onMostrarNumerosChange={setMostrarNumeros}
            />

            {/* Ticket PDF focus */}
            {sorteosEnabled && (
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  <strong>{t('raffle.ticketFocus')}</strong>
                </label>
                <select
                  className='h-9 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={ticketFocus}
                  onChange={e => setTicketFocus(e.target.value)}
                >
                  <option value='sorteos'>{t('raffle.ticketFocusSorteos')}</option>
                  <option value='premios'>{t('raffle.ticketFocusPremios')}</option>
                </select>
                <p className='text-xs text-muted-foreground'>{t('raffle.ticketFocusHint')}</p>
              </div>
            )}

            {/* Name + Total Tickets (standard only) */}
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>{t('common.name')}</label>
                <input
                  type='text'
                  required
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                />
              </div>

              {tipo === 'standard' && (
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('raffle.ticketQuantity')}
                  </label>
                  <input
                    type='number'
                    required
                    min='1'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={totalTickets}
                    onChange={e => setTotalTickets(parseInt(e.target.value) || 0)}
                  />
                </div>
              )}
            </div>

            {/* Rifa image */}
            <div className='grid gap-4 sm:grid-cols-2 items-end'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.uploadRaffleImage')}
                </label>
                <input
                  type='file'
                  className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                  onChange={uploadRifaPicture}
                  name='rifaImage'
                />
              </div>
              <div>
                <img
                  alt={t('raffle.raffleImage')}
                  src={rifaImage?.fileLink || '/images/emptyPhoto.png'}
                  style={{ maxWidth: '100px', maxHeight: '100px' }}
                />
              </div>
            </div>

            {/* Ticket price (standard only) */}
            {tipo === 'standard' && (
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.ticketPrice')}
                </label>
                <input
                  type='number'
                  required
                  min='1'
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={precioTicket}
                  onChange={e => setPrecioTicket(parseInt(e.target.value) || 0)}
                />
              </div>
            )}

            {/* Lottery acknowledgment (standard only) */}
            {tipo === 'standard' && <LotteryAcknowledgment precioTicket={precioTicket} />}

            {/* Ticket image */}
            <div className='grid gap-4 sm:grid-cols-2 items-end'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.uploadTicketImage')}
                </label>
                <input
                  type='file'
                  className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                  onChange={uploadPicture}
                  name='ticketImage'
                />
              </div>
              <div>
                <img
                  alt={t('raffle.ticketImage')}
                  src={ticketImage?.fileLink || '/images/emptyPhoto.png'}
                  style={{ maxWidth: '100px', maxHeight: '100px' }}
                />
              </div>
            </div>

            {/* Description */}
            <div className='space-y-1.5'>
              <label className='text-sm font-medium text-foreground'>
                {t('common.description')}
              </label>
              <input
                type='text'
                required
                className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                value={descripcion}
                onChange={e => setDescripcion(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      </form>

      {/* ── BOARD CATEGORY CONFIGURATION ───────────────────── */}
      {tipo === 'board' && (
        <div className='space-y-4'>
          <BoardModeFields
            t={t}
            boardMode={boardMode}
            onModeChange={setBoardMode}
            sharedBoard={sharedBoard}
            onSharedBoardChange={setSharedBoard}
          />
          <h4 className='text-base font-semibold text-foreground mt-4 mb-2'>
            {isSharedBoard
              ? t('raffle.plans', 'Planes')
              : t('raffle.boardCategories') || 'Board Categories'}
            {categories.length > 0 && (
              <span className='text-sm text-muted-foreground ml-2'>
                ({computedTotalTickets} {t('raffle.totalTickets') || 'total tickets'})
              </span>
            )}
          </h4>

          {/* Board sold square image */}
          <div className='grid gap-4 sm:grid-cols-2 items-end'>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium text-foreground'>
                {t('raffle.boardSoldSquareImage')}
              </label>
              <input
                type='file'
                accept='image/*'
                className='flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm file:mr-3 file:border-0 file:bg-transparent file:text-sm file:font-medium focus:outline-none focus:ring-2 focus:ring-ring'
                onChange={uploadBoardSoldPicture}
                name='imagenTableroVendido'
              />
              <p className='text-xs text-muted-foreground'>
                {t('raffle.boardSoldSquareImageHint')}
              </p>
            </div>
            <div>
              <img
                alt=''
                src={imagenTableroVendido?.fileLink || '/images/emptyPhoto.png'}
                style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'cover' }}
              />
            </div>
          </div>

          {/* Category list */}
          <div className='space-y-2'>
            {categories.map((cat, index) => (
              <div
                key={index}
                className={`flex items-center justify-between rounded-lg border p-3 ${
                  editingCategoryIndex === index ? 'border-ring border-2' : 'border-border'
                }`}
              >
                <div className='flex items-center gap-2'>
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      backgroundColor: cat.color,
                      display: 'inline-block',
                      border: '1px solid #ccc',
                    }}
                  />
                  <div>
                    <strong>{cat.nombre}</strong>
                    {!isSharedBoard && (
                      <span className='text-muted-foreground ml-2 text-sm'>
                        {cat.filas}×{cat.columnas} = {cat.filas * cat.columnas}{' '}
                        {t('raffle.squares') || 'squares'}
                      </span>
                    )}
                    <Badge variant='secondary' className='ml-2'>
                      ${cat.precioCasilla}/sq
                    </Badge>
                    <Badge variant='info' className='ml-1'>
                      {cat.ticketsPorCasilla} ticket{cat.ticketsPorCasilla !== 1 ? 's' : ''}/sq
                    </Badge>
                  </div>
                </div>
                <div className='flex gap-2'>
                  <Button
                    variant='outline'
                    size='icon-sm'
                    title={t('common.edit')}
                    onClick={() => handleEditCategory(index)}
                  >
                    <Pencil className='h-3.5 w-3.5' />
                  </Button>
                  <Button
                    variant='destructive'
                    size='icon-sm'
                    onClick={() => handleRemoveCategory(index)}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Category builder */}
          <Card>
            <CardHeader>
              <CardTitle>
                {editingCategoryIndex !== null
                  ? isSharedBoard
                    ? t('raffle.updatePlan', 'Actualizar plan')
                    : t('raffle.updateCategory')
                  : isSharedBoard
                    ? t('raffle.addPlan', 'Agregar plan')
                    : t('raffle.addCategory')}
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid gap-4 sm:grid-cols-4'>
                <div className='space-y-1.5 sm:col-span-2'>
                  <label className='text-sm font-medium text-foreground'>{t('common.name')}</label>
                  <input
                    type='text'
                    placeholder='Oro'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentCategory.nombre}
                    onChange={e =>
                      setCurrentCategory({ ...currentCategory, nombre: e.target.value })
                    }
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('common.color') || 'Color'}
                  </label>
                  <input
                    type='color'
                    className='h-9 w-16 rounded-md border border-input cursor-pointer'
                    value={currentCategory.color}
                    onChange={e =>
                      setCurrentCategory({ ...currentCategory, color: e.target.value })
                    }
                  />
                </div>
                {!isSharedBoard && (
                  <div className='space-y-1.5'>
                    <label className='text-sm font-medium text-foreground'>
                      {t('raffle.rows') || 'Rows'}
                    </label>
                    <input
                      type='number'
                      min='1'
                      className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                      value={currentCategory.filas}
                      onChange={e =>
                        setCurrentCategory({
                          ...currentCategory,
                          filas: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                )}
              </div>
              <div className='grid gap-4 sm:grid-cols-3'>
                {!isSharedBoard && (
                  <div className='space-y-1.5'>
                    <label className='text-sm font-medium text-foreground'>
                      {t('raffle.columns') || 'Columns'}
                    </label>
                    <input
                      type='number'
                      min='1'
                      className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                      value={currentCategory.columnas}
                      onChange={e =>
                        setCurrentCategory({
                          ...currentCategory,
                          columnas: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                  </div>
                )}
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('raffle.pricePerSquare') || 'Price per Square'}
                  </label>
                  <input
                    type='number'
                    min='1'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentCategory.precioCasilla}
                    onChange={e =>
                      setCurrentCategory({
                        ...currentCategory,
                        precioCasilla: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('raffle.ticketsPerSquare') || 'Tickets per Square'}
                  </label>
                  <input
                    type='number'
                    min='0'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentCategory.ticketsPorCasilla}
                    onChange={e =>
                      setCurrentCategory({
                        ...currentCategory,
                        ticketsPorCasilla: Math.max(0, parseInt(e.target.value) || 0),
                      })
                    }
                  />
                </div>
              </div>
              <div className='flex gap-2 flex-wrap'>
                <Button variant='secondary' onClick={handleSaveCategory}>
                  {editingCategoryIndex !== null ? (
                    <>
                      <Pencil className='h-4 w-4' />
                      {isSharedBoard
                        ? t('raffle.updatePlan', 'Actualizar plan')
                        : t('raffle.updateCategory')}
                    </>
                  ) : (
                    <>
                      <Plus className='h-4 w-4' />
                      {isSharedBoard
                        ? t('raffle.addPlan', 'Agregar plan')
                        : t('raffle.addCategory')}
                    </>
                  )}
                </Button>
                {editingCategoryIndex !== null && (
                  <Button variant='outline' onClick={cancelCategoryEdit}>
                    <X className='h-4 w-4' />
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── SORTEOS ─────────────────────────────────────────── */}
      {sorteosEnabled && (
        <div className='space-y-4'>
          <h4 className='text-base font-semibold text-foreground mt-4 mb-2'>
            {t('raffle.sorteos')}
          </h4>

          {/* List of saved sorteos */}
          <div className='space-y-2'>
            {sorteos.map((sorteo, index) => (
              <div
                key={index}
                className={`flex items-center justify-between rounded-lg border p-3 ${
                  editingSorteoIndex === index ? 'border-ring border-2' : 'border-border'
                }`}
              >
                <div>
                  <strong>{sorteo.titulo}</strong>{' '}
                  <span className='text-muted-foreground'>
                    - {new Date(sorteo.fecha).toLocaleDateString()}
                  </span>
                  <br />
                  <small className='text-muted-foreground'>
                    {sorteo.premios.length} {t('raffle.prizes')}
                  </small>
                </div>
                <div className='flex gap-2'>
                  <Button variant='outline' size='icon-sm' onClick={() => handleEditSorteo(index)}>
                    <Pencil className='h-3.5 w-3.5' />
                  </Button>
                  <Button
                    variant='destructive'
                    size='icon-sm'
                    onClick={() => handleRemoveSorteo(index)}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Sorteo builder card */}
          <Card className={editingSorteoIndex !== null ? 'ring-2 ring-ring' : ''}>
            <CardHeader>
              <CardTitle>
                {editingSorteoIndex !== null
                  ? `${t('raffle.editSorteo') || 'Edit Draw'} #${editingSorteoIndex + 1}`
                  : t('raffle.addSorteo')}
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>{t('common.title')}</label>
                  <input
                    type='text'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentSorteo.titulo}
                    onChange={e => setCurrentSorteo({ ...currentSorteo, titulo: e.target.value })}
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>{t('common.date')}</label>
                  <input
                    type='datetime-local'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentSorteo.fecha}
                    onChange={e => setCurrentSorteo({ ...currentSorteo, fecha: e.target.value })}
                  />
                </div>
              </div>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('raffle.salesTarget')}
                </label>
                <input
                  type='number'
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={currentSorteo.objetivoVenta}
                  onChange={e =>
                    setCurrentSorteo({
                      ...currentSorteo,
                      objetivoVenta: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>

              {/* Premio list inside current sorteo */}
              <div className='space-y-2'>
                {currentSorteo.premios.map((premio, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between rounded-lg border p-3 ${
                      editingPremioIndex === index ? 'border-ring border-2' : 'border-border'
                    }`}
                  >
                    <div>
                      <strong>
                        {premio.puesto}º - {premio.nombre}
                      </strong>
                      <br />
                      <small className='text-muted-foreground'>{premio.descripcion}</small>
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        variant='outline'
                        size='icon-sm'
                        onClick={() => handleEditPremio(index)}
                      >
                        <Pencil className='h-3.5 w-3.5' />
                      </Button>
                      <Button
                        variant='destructive'
                        size='icon-sm'
                        onClick={() => handleRemovePremio(index)}
                      >
                        <Trash2 className='h-3.5 w-3.5' />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Premio builder */}
              <h5 className='text-sm font-semibold text-foreground mb-3'>
                {editingPremioIndex !== null
                  ? `${t('raffle.editPrize') || 'Edit Prize'} #${editingPremioIndex + 1}`
                  : t('raffle.addPrize')}
              </h5>
              <div className='grid gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>{t('common.name')}</label>
                  <input
                    type='text'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentPremio.nombre}
                    onChange={e => setCurrentPremio({ ...currentPremio, nombre: e.target.value })}
                  />
                </div>
                <div className='space-y-1.5'>
                  <label className='text-sm font-medium text-foreground'>
                    {t('common.position')}
                  </label>
                  <input
                    type='number'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                    value={currentPremio.puesto}
                    onChange={e =>
                      setCurrentPremio({
                        ...currentPremio,
                        puesto: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
              </div>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium text-foreground'>
                  {t('common.description')}
                </label>
                <input
                  type='text'
                  className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring'
                  value={currentPremio.descripcion}
                  onChange={e =>
                    setCurrentPremio({ ...currentPremio, descripcion: e.target.value })
                  }
                />
              </div>
              <div className='flex gap-2'>
                <Button variant='secondary' size='sm' onClick={handleSavePremio}>
                  {editingPremioIndex !== null ? (
                    t('common.save') || 'Save Prize'
                  ) : (
                    <>
                      <Plus className='h-4 w-4' />
                      {t('raffle.addPrize')}
                    </>
                  )}
                </Button>
                {editingPremioIndex !== null && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setEditingPremioIndex(null);
                      setCurrentPremio({ ...emptyPremio });
                    }}
                  >
                    <X className='h-4 w-4' />
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Save / Cancel sorteo */}
          <div className='flex gap-3'>
            <Button variant='default' onClick={handleSaveSorteo}>
              {editingSorteoIndex !== null ? (
                t('raffle.saveSorteo') || 'Save Draw'
              ) : (
                <>
                  <Plus className='h-4 w-4' />
                  {t('raffle.addSorteo')}
                </>
              )}
            </Button>
            <Button variant='outline' onClick={clearCurrentSorteo}>
              <X className='h-4 w-4' />
              {editingSorteoIndex !== null ? t('common.cancel') : t('common.clear') || 'Clear'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddRifa;
